import React from 'react';
import {
    Alert,
    Autocomplete,
    Avatar,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import Layout from '../../components/Layout';
import { ExternalCatalogOption, IExternalBooking } from '../../types/bookings';
import { BulkCampOrderResult, Camp, CampDetails, CampSlot, PlaceCampOrderPayload } from '../../types/camps';
import { UserData } from '../../types/user';
import { ICorporate } from '../../types/corporates';
import { fetchExternalPackages, fetchExternalTests } from '../../services/externalBookings';
import { bulkPlaceCampOrders, fetchCampBookings, fetchCampDetails, fetchCamps, fetchCampSlots, placeCampOrder, updateCampCorporateIds } from '../../services/camps';
import { useCompanyStore } from '../../services/company';
import { useAppContext } from '../../services/context/AppContext';
import { showError, showSuccess } from '../../services/toaster';
import { doGET, doPOST } from '../../utils/HttpUtils';
import { getUserCorporateId, isHRUser } from '../../utils/helper';

const buildAppointment = (slot?: CampSlot) => {
    if (!slot?.date || !slot?.time) return '';
    return `${slot.date}T${slot.time}:00`;
};

const toBookingDate = (value: string) => value ? value.replace(/-/g, '') : '';

const formatBookingDate = (value?: string) => {
    if (!value) return '-';
    if (/^\d{8}$/.test(value)) {
        return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`;
    }
    return value;
};

const normalizeGender = (value?: string) => {
    const gender = value?.toUpperCase();
    if (gender === 'FEMALE') return 'FEMALE';
    if (gender === 'OTHER') return 'OTHER';
    return 'MALE';
};

const toUserProfileGender = (value?: string) => {
    const gender = value?.toUpperCase();
    if (gender === 'FEMALE') return 'Female';
    if (gender === 'OTHER') return 'Other';
    return 'Male';
};

const normalizeMobile = (value?: string) => {
    const digits = (value || '').replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('0')) {
        return digits.slice(1);
    }
    if (digits.length === 12 && digits.startsWith('91')) {
        return digits.slice(2);
    }
    return digits;
};

const toDateInputValue = (value?: string) => {
    if (!value) return '';
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
    if (/^\d{8}$/.test(value)) {
        return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`;
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
};

const getUserAgeValue = (user?: UserData | null) => {
    const age = user?.age;
    if (age === null || age === undefined) return '';
    return String(age).trim();
};

const normalizeUserProfile = (response: any): UserData | null => {
    const raw = response?.data;
    return raw?.data?.user || raw?.user || raw?.data || raw || null;
};

const defaultOrderForm = {
    userId: '',
    corporateId: '',
    name: '',
    mobile: '',
    gender: 'MALE',
    email: '',
    age: '',
    dateOfBirth: '',
    uniqueId: '',
    referenceId: '',
    labId: '',
    pincode: '',
    notes: '',
    altContact: '',
    isVip: false,
};

const defaultBulkForm = {
    labId: '',
    pincode: '',
    notes: '',
    isVip: false,
};

const defaultCreateUserForm = {
    name: '',
    email: '',
    phone: '',
    gender: 'Male',
};

const Camps = () => {
    const { userData } = useAppContext();
    const { globalCompanyId } = useCompanyStore();
    const isHR = isHRUser(userData);
    const hrCorporateId = getUserCorporateId(userData);
    const [startDate, setStartDate] = React.useState('');
    const [endDate, setEndDate] = React.useState('');
    const [camps, setCamps] = React.useState<Camp[]>([]);
    const [selectedCamp, setSelectedCamp] = React.useState<Camp | null>(null);
    const [details, setDetails] = React.useState<CampDetails | null>(null);
    const [slots, setSlots] = React.useState<CampSlot[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [detailsLoading, setDetailsLoading] = React.useState(false);
    const [orderOpen, setOrderOpen] = React.useState(false);
    const [campDetailsOpen, setCampDetailsOpen] = React.useState(false);
    const [bookingChoiceOpen, setBookingChoiceOpen] = React.useState(false);
    const [selectedSlotKey, setSelectedSlotKey] = React.useState('');
    const [saving, setSaving] = React.useState(false);
    const [orderForm, setOrderForm] = React.useState(defaultOrderForm);
    const [orderUserType, setOrderUserType] = React.useState<'normal' | 'corporate'>('normal');
    const [orderUserOptions, setOrderUserOptions] = React.useState<UserData[]>([]);
    const [selectedOrderUser, setSelectedOrderUser] = React.useState<UserData | null>(null);
    const [orderUsersLoading, setOrderUsersLoading] = React.useState(false);
    const [orderUserSearch, setOrderUserSearch] = React.useState('');
    const [createUserOpen, setCreateUserOpen] = React.useState(false);
    const [createUserSaving, setCreateUserSaving] = React.useState(false);
    const [createUserForm, setCreateUserForm] = React.useState(defaultCreateUserForm);
    const [corporateOptions, setCorporateOptions] = React.useState<ICorporate[]>([]);
    const [selectedOrderCorporate, setSelectedOrderCorporate] = React.useState<ICorporate | null>(null);
    const [corporatesLoading, setCorporatesLoading] = React.useState(false);
    const [corporateSearch, setCorporateSearch] = React.useState('');
    const [orderPackages, setOrderPackages] = React.useState<ExternalCatalogOption[]>([]);
    const [orderTests, setOrderTests] = React.useState<ExternalCatalogOption[]>([]);
    const [bulkForm, setBulkForm] = React.useState(defaultBulkForm);
    const [bulkFile, setBulkFile] = React.useState<File | null>(null);
    const [bulkSaving, setBulkSaving] = React.useState(false);
    const [bulkResult, setBulkResult] = React.useState<BulkCampOrderResult | null>(null);
    const [catalogLoading, setCatalogLoading] = React.useState(false);
    const [packageOptions, setPackageOptions] = React.useState<ExternalCatalogOption[]>([]);
    const [testOptions, setTestOptions] = React.useState<ExternalCatalogOption[]>([]);
    const [bulkPackages, setBulkPackages] = React.useState<ExternalCatalogOption[]>([]);
    const [bulkTests, setBulkTests] = React.useState<ExternalCatalogOption[]>([]);
    const [bookingDateFrom, setBookingDateFrom] = React.useState('');
    const [bookingDateTo, setBookingDateTo] = React.useState('');
    const [campBookings, setCampBookings] = React.useState<IExternalBooking[]>([]);
    const [bookingsLoading, setBookingsLoading] = React.useState(false);
    const [bookingsTotal, setBookingsTotal] = React.useState(0);
    const [bookingPage, setBookingPage] = React.useState(0);
    const [bookingRowsPerPage, setBookingRowsPerPage] = React.useState(10);
    const [activeTab, setActiveTab] = React.useState<'bookings' | 'camps'>('bookings');
    const [allCorporates, setAllCorporates] = React.useState<ICorporate[]>([]);
    const [visibilityCamp, setVisibilityCamp] = React.useState<Camp | null>(null);
    const [visibilityOpen, setVisibilityOpen] = React.useState(false);
    const [visibilityCorporates, setVisibilityCorporates] = React.useState<ICorporate[]>([]);
    const [visibilitySaving, setVisibilitySaving] = React.useState(false);

    const scopedCorporateId = React.useMemo(() => {
        if (isHR && hrCorporateId) return hrCorporateId;
        if (globalCompanyId && globalCompanyId !== 'general') return globalCompanyId;
        return '';
    }, [globalCompanyId, hrCorporateId, isHR]);

    const selectedSlot = React.useMemo(
        () => slots.find((slot) => `${slot.date}|${slot.time}` === selectedSlotKey),
        [slots, selectedSlotKey]
    );

    const loadCamps = React.useCallback(async () => {
        setLoading(true);
        try {
            const data = await fetchCamps({ startDate, endDate, corporateId: scopedCorporateId || undefined });
            setCamps(data);
            if (!data.find((camp) => camp.campLsId === selectedCamp?.campLsId)) {
                setSelectedCamp(null);
                setDetails(null);
                setSlots([]);
                setCampDetailsOpen(false);
            }
        } finally {
            setLoading(false);
        }
    }, [endDate, scopedCorporateId, selectedCamp?.campLsId, startDate]);

    React.useEffect(() => {
        loadCamps();
    }, []);

    const loadAllCorporates = React.useCallback(async () => {
        try {
            // Backend caps `limit` at 100, so page through everything instead of
            // requesting one oversized page.
            const PAGE_SIZE = 100;
            const first = await doGET(`/corporates?page=1&limit=${PAGE_SIZE}`);
            const firstRows = first?.data?.data;
            const all: ICorporate[] = Array.isArray(firstRows) ? firstRows : [];
            const totalPages = first?.data?.pagination?.totalPages || 1;

            if (totalPages > 1) {
                const rest = await Promise.all(
                    Array.from({ length: totalPages - 1 }, (_, index) =>
                        doGET(`/corporates?page=${index + 2}&limit=${PAGE_SIZE}`)
                    )
                );
                rest.forEach((response) => {
                    const rows = response?.data?.data;
                    if (Array.isArray(rows)) all.push(...rows);
                });
            }

            setAllCorporates(all);
        } catch (_error) {
            // Non-critical: visibility editing just won't have corporate names to pick from.
        }
    }, []);

    React.useEffect(() => {
        if (!isHR) {
            loadAllCorporates();
        }
    }, [isHR, loadAllCorporates]);

    const corporateNameById = React.useMemo(() => {
        const map = new Map<string, string>();
        allCorporates.forEach((corporate) => {
            if (corporate._id) {
                map.set(corporate._id, corporate.name || corporate.companyName || corporate._id);
            }
        });
        return map;
    }, [allCorporates]);

    const openVisibilityDialog = (camp: Camp, event?: React.MouseEvent) => {
        event?.stopPropagation();
        setVisibilityCamp(camp);
        setVisibilityCorporates(
            (camp.corporateIds || [])
                .map((id) => allCorporates.find((corporate) => corporate._id === id) || { _id: id, name: corporateNameById.get(id) || id })
        );
        setVisibilityOpen(true);
    };

    const saveVisibility = async () => {
        if (!visibilityCamp) return;
        setVisibilitySaving(true);
        try {
            const corporateIds = visibilityCorporates.map((corporate) => corporate._id).filter(Boolean) as string[];
            const result = await updateCampCorporateIds(visibilityCamp.campLsId, corporateIds);
            if (result) {
                setCamps((prev) => prev.map((camp) =>
                    camp.campLsId === visibilityCamp.campLsId ? { ...camp, corporateIds: result.corporateIds } : camp
                ));
                setVisibilityOpen(false);
            }
        } finally {
            setVisibilitySaving(false);
        }
    };

    const loadCampBookings = React.useCallback(async () => {
        setBookingsLoading(true);
        try {
            const result = await fetchCampBookings({
                bookingDateFrom: toBookingDate(bookingDateFrom),
                bookingDateTo: toBookingDate(bookingDateTo),
                page: bookingPage + 1,
                limit: bookingRowsPerPage,
                corporateId: scopedCorporateId || undefined,
            });
            const filteredRows = scopedCorporateId
                ? result.rows.filter((booking) => booking.corporateId === scopedCorporateId)
                : result.rows;
            setCampBookings(filteredRows);
            setBookingsTotal(scopedCorporateId ? filteredRows.length : result.total);
        } finally {
            setBookingsLoading(false);
        }
    }, [bookingDateFrom, bookingDateTo, bookingPage, bookingRowsPerPage, scopedCorporateId]);

    React.useEffect(() => {
        loadCampBookings();
    }, [loadCampBookings]);

    React.useEffect(() => {
        const loadCatalog = async () => {
            setCatalogLoading(true);
            try {
                const [packages, tests] = await Promise.all([
                    selectedCamp?.campLsId
                        ? fetchExternalPackages(undefined, undefined, 'labstack', selectedCamp.campLsId)
                        : Promise.resolve([]),
                    fetchExternalTests({ integration: 'labstack' }),
                ]);
                setPackageOptions(packages);
                setTestOptions(tests);
            } finally {
                setCatalogLoading(false);
            }
        };

        loadCatalog();
    }, [selectedCamp?.campLsId]);

    const loadOrderUsers = React.useCallback(async () => {
        if (orderUserType === 'corporate' && !selectedOrderCorporate?._id) {
            setOrderUserOptions([]);
            return;
        }

        setOrderUsersLoading(true);
        try {
            const params = new URLSearchParams();
            params.set('page', '1');
            params.set('limit', '50');
            params.set('userType', 'user');
            if (orderUserSearch.trim()) {
                params.set('name', orderUserSearch.trim());
            }
            if (scopedCorporateId) {
                params.set('corporateId', scopedCorporateId);
            } else if (orderUserType === 'corporate' && selectedOrderCorporate?._id) {
                params.set('corporateId', selectedOrderCorporate._id);
            } else {
                params.set('forUser', 'true');
            }

            const response = await doGET(`/users?${params.toString()}`);
            const rows = response?.data?.data?.users ?? response?.data?.data ?? [];
            setOrderUserOptions(Array.isArray(rows) ? rows : []);
        } finally {
            setOrderUsersLoading(false);
        }
    }, [orderUserSearch, orderUserType, scopedCorporateId, selectedOrderCorporate?._id]);

    React.useEffect(() => {
        if (orderOpen) {
            loadOrderUsers();
        }
    }, [loadOrderUsers, orderOpen]);

    const loadCorporates = React.useCallback(async () => {
        setCorporatesLoading(true);
        try {
            if (isHR && hrCorporateId) {
                const response = await doGET(`/corporates/${hrCorporateId}`);
                const corporate = response?.data?.data || response?.data || null;
                setCorporateOptions(corporate ? [corporate] : []);
                return;
            }

            const params = new URLSearchParams();
            params.set('page', '1');
            params.set('limit', '50');
            if (corporateSearch.trim()) {
                params.set('search', corporateSearch.trim());
            }

            const response = await doGET(`/corporates?${params.toString()}`);
            const rows = response?.data?.data;
            setCorporateOptions(Array.isArray(rows) ? rows : []);
        } finally {
            setCorporatesLoading(false);
        }
    }, [corporateSearch, hrCorporateId, isHR]);

    React.useEffect(() => {
        if (orderOpen && orderUserType === 'corporate') {
            loadCorporates();
        }
    }, [loadCorporates, orderOpen, orderUserType]);

    React.useEffect(() => {
        if (!isHR || !hrCorporateId) return;

        setOrderUserType('corporate');
        setSelectedOrderCorporate((prev) => prev || ({
            ...(userData?.corporate || {}),
            _id: userData?.corporate?._id || hrCorporateId,
            name: userData?.corporate?.name || userData?.companyName || '',
            companyName: userData?.corporate?.companyName || '',
        }));
        setOrderForm((prev) => ({
            ...prev,
            corporateId: hrCorporateId,
        }));
    }, [hrCorporateId, isHR, userData?.companyName, userData?.corporate]);

    const loadCampContext = async (camp: Camp) => {
        setSelectedCamp(camp);
        setDetails(null);
        setSlots([]);
        setSelectedSlotKey('');
        setDetailsLoading(true);
        try {
            const [campDetails, campSlots] = await Promise.all([
                fetchCampDetails(camp.campLsId),
                fetchCampSlots(camp.campLsId),
            ]);
            setDetails(campDetails);
            setSlots(campSlots);
            const firstSlot = campSlots[0];
            if (firstSlot) {
                setSelectedSlotKey(`${firstSlot.date}|${firstSlot.time}`);
            }
            setBulkForm({
                ...defaultBulkForm,
                labId: camp.lsLabId ? String(camp.lsLabId) : '',
                pincode: campDetails?.pincode || '',
            });
            setBulkFile(null);
            setBulkResult(null);
            setBulkPackages([]);
            setBulkTests([]);
            return { campDetails, campSlots };
        } finally {
            setDetailsLoading(false);
        }
    };

    const selectCamp = (camp: Camp) => {
        setSelectedCamp(camp);
        setDetails(null);
        setSlots([]);
        setSelectedSlotKey('');
        setBulkFile(null);
        setBulkResult(null);
        setBulkPackages([]);
        setBulkTests([]);
        setCampDetailsOpen(false);
        setOrderOpen(false);
        setBookingChoiceOpen(true);
    };

    const openIndividualBooking = async () => {
        if (!selectedCamp) return;
        setBookingChoiceOpen(false);
        const result = await loadCampContext(selectedCamp);
        setSelectedOrderUser(null);
        if (isHR && hrCorporateId) {
            const response = await doGET(`/corporates/${hrCorporateId}`);
            const corporate = response?.data?.data || response?.data || null;
            setSelectedOrderCorporate(corporate);
        } else {
            setSelectedOrderCorporate(null);
        }
        setOrderUserSearch('');
        setCorporateSearch('');
        setOrderPackages([]);
        setOrderTests([]);
        setOrderForm({
            ...defaultOrderForm,
            corporateId: isHR ? hrCorporateId : '',
            labId: selectedCamp.lsLabId ? String(selectedCamp.lsLabId) : '',
            pincode: result?.campDetails?.pincode || '',
        });
        setOrderOpen(true);
    };

    const openBulkBooking = async () => {
        if (!selectedCamp) return;
        setBookingChoiceOpen(false);
        setCampDetailsOpen(true);
        await loadCampContext(selectedCamp);
    };

    const updateOrderForm = (field: keyof typeof defaultOrderForm, value: string | boolean) => {
        setOrderForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleOrderUserTypeChange = (value: 'normal' | 'corporate') => {
        if (isHR) return;
        setOrderUserType(value);
        setSelectedOrderUser(null);
        setSelectedOrderCorporate(null);
        setOrderUserSearch('');
        setCorporateSearch('');
        setOrderUserOptions([]);
        setOrderForm((prev) => ({
            ...prev,
            userId: '',
            corporateId: isHR ? hrCorporateId : '',
            name: '',
            mobile: '',
            gender: 'MALE',
            email: '',
            age: '',
            dateOfBirth: '',
            uniqueId: '',
            pincode: details?.pincode || '',
        }));
    };

    const handleOrderCorporateSelect = (corporate: ICorporate | null) => {
        setSelectedOrderCorporate(corporate);
        setSelectedOrderUser(null);
        setOrderUserSearch('');
        setOrderUserOptions([]);
        setOrderForm((prev) => ({
            ...prev,
            corporateId: corporate?._id || '',
            userId: '',
            name: '',
            mobile: '',
            gender: 'MALE',
            email: '',
            age: '',
            dateOfBirth: '',
            uniqueId: '',
            pincode: corporate?.pincode || details?.pincode || prev.pincode,
        }));
    };

    const updateCreateUserForm = (field: keyof typeof defaultCreateUserForm, value: string) => {
        setCreateUserForm((prev) => ({ ...prev, [field]: value }));
    };

    const openCreateUserDialog = () => {
        if (orderUserType === 'corporate' && !selectedOrderCorporate && !scopedCorporateId) {
            showError('Select a corporate first');
            return;
        }

        setCreateUserForm({
            name: orderForm.name,
            email: orderForm.email,
            phone: orderForm.mobile,
            gender: toUserProfileGender(orderForm.gender),
        });
        setCreateUserOpen(true);
    };

    const fillOrderFormFromUser = (user: UserData | null) => {
        const userId = user?._id || '';
        setOrderForm((prev) => ({
            ...prev,
            userId,
            uniqueId: userId,
            corporateId: user?.corporateId || prev.corporateId,
            name: user?.name || '',
            mobile: user?.phone || '',
            gender: normalizeGender(user?.gender),
            email: ((user as any)?.corporateEmail || user?.email || '') as string,
            age: getUserAgeValue(user),
            dateOfBirth: toDateInputValue(user?.dateOfBirth),
            pincode: user?.pincode || details?.pincode || prev.pincode,
        }));
    };

    const handleOrderUserSelect = async (user: UserData | null) => {
        setSelectedOrderUser(user);
        fillOrderFormFromUser(user);

        if (!user?._id) return;

        try {
            const response = await doGET(`/users/${user._id}`);
            const detailedUser = normalizeUserProfile(response);
            if (detailedUser) {
                fillOrderFormFromUser(detailedUser);
                setSelectedOrderUser((prev) => prev ? { ...prev, ...detailedUser } : detailedUser);
            }
        } catch (_error) {
            // Keep the list result if detail fetch fails.
        }
    };

    const createUserFromOrder = async () => {
        const name = createUserForm.name.trim();
        const email = createUserForm.email.trim();
        const phone = normalizeMobile(createUserForm.phone);

        if (!name || !email || !phone) {
            showError('Please enter name, email, and mobile');
            return;
        }
        if (!/^[1-9]\d{9}$/.test(phone)) {
            showError('Mobile must be a valid 10-digit number');
            return;
        }

        const payload: Record<string, string> = {
            name,
            email,
            phone,
            gender: createUserForm.gender,
            userType: 'user',
        };
        const corporateId = scopedCorporateId || selectedOrderCorporate?._id || '';
        if (corporateId) {
            payload.corporateId = corporateId;
        }

        setCreateUserSaving(true);
        try {
            const response = await doPOST('/users', payload);
            if (response.status < 200 || response.status >= 400) {
                showError(response.message || 'Failed to create user');
                return;
            }

            const createdUser = normalizeUserProfile(response);
            if (!createdUser?._id) {
                showError('User created but response was incomplete');
                return;
            }

            showSuccess('User created successfully');
            setSelectedOrderUser(createdUser);
            setOrderUserOptions((prev) => [createdUser, ...prev.filter((item) => item._id !== createdUser._id)]);
            fillOrderFormFromUser(createdUser);
            setCreateUserOpen(false);
        } catch (_error) {
            showError('Failed to create user');
        } finally {
            setCreateUserSaving(false);
        }
    };

    const updateBulkForm = (field: keyof typeof defaultBulkForm, value: string | boolean) => {
        setBulkForm((prev) => ({ ...prev, [field]: value }));
    };

    const downloadTemplate = () => {
        const headers = ['phone'];
        const sample = ['9876543210'];
        const csv = [headers.join(','), sample.join(',')].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'camp-users-template.csv';
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    };

    const submitOrder = async () => {
        if (!selectedCamp || !selectedSlot) return;
        if (!orderPackages.length) {
            showError('Please select at least one package');
            return;
        }

        const age = orderForm.age.trim();
        const dateOfBirth = orderForm.dateOfBirth.trim();
        const mobile = normalizeMobile(orderForm.mobile);
        if (!age && !dateOfBirth) {
            showError('Please provide age or date of birth');
            return;
        }
        if (!/^[1-9]\d{9}$/.test(mobile)) {
            showError('Mobile must be a valid 10-digit number');
            return;
        }

        const payload: PlaceCampOrderPayload = {
            campLsId: selectedCamp.campLsId,
            orderType: 'CENTER_VISIT',
            userId: orderForm.userId.trim(),
            corporateId: orderForm.corporateId.trim() || undefined,
            appointment: buildAppointment(selectedSlot),
            name: orderForm.name.trim(),
            mobile,
            gender: orderForm.gender,
            email: orderForm.email.trim() || undefined,
            age: age || undefined,
            dateOfBirth: dateOfBirth || undefined,
            packageIds: orderPackages.map((item) => item.id),
            uniqueId: orderForm.uniqueId.trim() || undefined,
            referenceId: orderForm.referenceId.trim() || undefined,
            labId: orderForm.labId.trim() || undefined,
            pincode: orderForm.pincode.trim() || undefined,
            labName: selectedCamp.labName,
            notes: orderForm.notes.trim() || undefined,
            altContact: orderForm.altContact.trim() || undefined,
            isVip: orderForm.isVip,
        };
        if (orderTests.length) {
            payload.testIds = orderTests.map((item) => item.id);
        }

        setSaving(true);
        try {
            const created = await placeCampOrder(payload);
            if (created) {
                setOrderOpen(false);
                loadCampBookings();
            }
        } finally {
            setSaving(false);
        }
    };

    const submitBulkOrders = async () => {
        if (!selectedCamp || !selectedSlot || !bulkFile) return;
        if (!bulkPackages.length) {
            showError('Please select at least one package');
            return;
        }

        setBulkSaving(true);
        try {
            const payload: {
                campLsId: string;
                orderType: string;
                appointment: string;
                file: File;
                packageIds: string[];
                testIds: string[];
                labId?: string;
                pincode?: string;
                labName?: string;
                notes?: string;
                isVip: boolean;
            } = {
                campLsId: selectedCamp.campLsId,
                orderType: 'CENTER_VISIT',
                appointment: buildAppointment(selectedSlot),
                file: bulkFile,
                packageIds: bulkPackages.map((item) => item.id),
                testIds: bulkTests.map((item) => item.id),
                labId: bulkForm.labId.trim() || undefined,
                pincode: bulkForm.pincode.trim() || undefined,
                labName: selectedCamp.labName,
                notes: bulkForm.notes.trim() || undefined,
                isVip: bulkForm.isVip,
            };

            const result = await bulkPlaceCampOrders(payload);
            setBulkResult(result);
            if (result?.createdCount) {
                loadCampBookings();
            }
        } finally {
            setBulkSaving(false);
        }
    };

    return (
        <Layout appBarTitle="Camps">
            <Box sx={{ display: 'grid', gap: 2 }}>
                <Paper variant="outlined" sx={{ px: 1 }}>
                    <Tabs
                        value={activeTab}
                        onChange={(_event, value) => setActiveTab(value)}
                    >
                        <Tab label="Camp Bookings" value="bookings" />
                        <Tab label="Camps" value="camps" />
                    </Tabs>
                </Paper>

                {isHR && scopedCorporateId && (
                    <Alert severity="info">
                        Camps is locked to your corporate scope only.
                    </Alert>
                )}

                {activeTab === 'camps' && (
                    <>
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', lg: 'center' }} spacing={2}>
                        <Box>
                            <Typography variant="h6">LabStack Camps</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Select a camp, choose a slot, and create camp bookings.
                            </Typography>
                        </Box>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
                            <TextField
                                type="date"
                                size="small"
                                label="Start"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                type="date"
                                size="small"
                                label="End"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                            <Button variant="outlined" onClick={loadCamps} disabled={loading}>
                                {loading ? 'Loading...' : 'Refresh'}
                            </Button>
                        </Stack>
                    </Stack>
                </Paper>

                <TableContainer component={Paper} variant="outlined" sx={{ overflow: 'hidden' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Logo</TableCell>
                                <TableCell>Camp ID</TableCell>
                                <TableCell>Corporate</TableCell>
                                <TableCell>Dates</TableCell>
                                <TableCell>Lab</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Orders</TableCell>
                                <TableCell>Visible To</TableCell>
                                <TableCell align="center">Book</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {camps.map((camp) => (
                                <TableRow
                                    key={camp.campLsId}
                                    hover
                                    selected={selectedCamp?.campLsId === camp.campLsId}
                                    onClick={() => selectCamp(camp)}
                                    sx={{
                                        cursor: 'pointer',
                                        '&.Mui-selected': {
                                            backgroundColor: 'rgba(0, 150, 150, 0.08)',
                                        },
                                    }}
                                >
                                    <TableCell>
                                        <Avatar
                                            src={camp.labLogo || undefined}
                                            alt={camp.labName || camp.campLsId}
                                            variant="rounded"
                                            sx={{
                                                width: 36,
                                                height: 36,
                                                bgcolor: 'primary.light',
                                                color: 'primary.contrastText',
                                                fontSize: 14,
                                            }}
                                        >
                                            {(camp.labName || camp.campLsId || 'L').charAt(0).toUpperCase()}
                                        </Avatar>
                                    </TableCell>
                                    <TableCell>{camp.campLsId}</TableCell>
                                    <TableCell>{camp.corporateName || '-'}</TableCell>
                                    <TableCell>{camp.dates?.join(', ') || '-'}</TableCell>
                                    <TableCell>{camp.labName || '-'}</TableCell>
                                    <TableCell>
                                        {camp.campStatus ? <Chip size="small" label={camp.campStatus} /> : '-'}
                                    </TableCell>
                                    <TableCell align="right">{camp.numOrders ?? '-'}</TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={0.5} alignItems="center" useFlexGap flexWrap="wrap">
                                            {!camp.corporateIds?.length ? (
                                                <Chip size="small" label="Everyone" variant="outlined" />
                                            ) : (
                                                camp.corporateIds.map((id) => (
                                                    <Chip key={id} size="small" label={corporateNameById.get(id) || id} />
                                                ))
                                            )}
                                            {!isHR && (
                                                <Tooltip title="Edit visibility">
                                                    <IconButton size="small" onClick={(event) => openVisibilityDialog(camp, event)}>
                                                        <EditRoundedIcon fontSize="inherit" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Stack>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Open booking">
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    selectCamp(camp);
                                                }}
                                            >
                                                <ArrowForwardRoundedIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {!camps.length && (
                                <TableRow>
                                    <TableCell colSpan={9}>
                                        <Typography variant="body2" color="text.secondary" align="center">
                                            {loading ? 'Loading camps...' : 'No camps found'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                    </>
                )}

                {activeTab === 'bookings' && (
                <Paper variant="outlined" sx={{ p: 2, display: 'grid', gap: 2 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2}>
                        <Box>
                            <Typography variant="h6">Camp Bookings</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {bookingsLoading ? 'Loading bookings...' : `${bookingsTotal} camp bookings found`}
                            </Typography>
                        </Box>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <TextField
                                type="date"
                                size="small"
                                label="From"
                                value={bookingDateFrom}
                                onChange={(e) => {
                                    setBookingDateFrom(e.target.value);
                                    setBookingPage(0);
                                }}
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                type="date"
                                size="small"
                                label="To"
                                value={bookingDateTo}
                                onChange={(e) => {
                                    setBookingDateTo(e.target.value);
                                    setBookingPage(0);
                                }}
                                InputLabelProps={{ shrink: true }}
                            />
                            <Button variant="outlined" onClick={loadCampBookings} disabled={bookingsLoading}>
                                Refresh
                            </Button>
                        </Stack>
                    </Stack>

                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Time</TableCell>
                                    <TableCell>Patient</TableCell>
                                    <TableCell>Phone</TableCell>
                                    <TableCell>Camp ID</TableCell>
                                    <TableCell>Lab</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>External ID</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {campBookings.map((booking) => (
                                    <TableRow key={booking._id} hover>
                                        <TableCell>{formatBookingDate(booking.bookingDate)}</TableCell>
                                        <TableCell>{booking.bookingTime || '-'}</TableCell>
                                        <TableCell>{booking.patientName || '-'}</TableCell>
                                        <TableCell>{booking.patientPhone || '-'}</TableCell>
                                        <TableCell>{booking.meta?.campLsId || booking.externalProviderId || '-'}</TableCell>
                                        <TableCell>{booking.labName || '-'}</TableCell>
                                        <TableCell>{booking.status || '-'}</TableCell>
                                        <TableCell>{booking.externalBookingId || '-'}</TableCell>
                                    </TableRow>
                                ))}
                                {!campBookings.length && (
                                    <TableRow>
                                        <TableCell colSpan={8}>
                                            <Typography variant="body2" color="text.secondary" align="center">
                                                {bookingsLoading ? 'Loading camp bookings...' : 'No camp bookings found'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={bookingsTotal}
                        page={bookingPage}
                        rowsPerPage={bookingRowsPerPage}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        onPageChange={(_event, page) => setBookingPage(page)}
                        onRowsPerPageChange={(event) => {
                            setBookingRowsPerPage(parseInt(event.target.value, 10));
                            setBookingPage(0);
                        }}
                    />
                </Paper>
                )}
            </Box>

            <Dialog open={bookingChoiceOpen} onClose={() => setBookingChoiceOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                        <Typography variant="h6">Create Camp Booking</Typography>
                        <Button onClick={() => setBookingChoiceOpen(false)}>Cancel</Button>
                    </Stack>
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: 'grid', gap: 1 }}>
                        <Typography variant="subtitle2">{selectedCamp?.campLsId || '-'}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {selectedCamp?.corporateName || 'Corporate not provided'}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            Choose how you want to create the booking.
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, gap: 1.5 }}>
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={openIndividualBooking}
                        disabled={detailsLoading}
                    >
                        Individual Booking
                    </Button>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={openBulkBooking}
                        disabled={detailsLoading}
                    >
                        Bulk Booking
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={campDetailsOpen} onClose={() => setCampDetailsOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2}>
                        <Box>
                            <Typography variant="h6">{selectedCamp?.campLsId || 'Camp Details'}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {selectedCamp?.corporateName || 'Corporate not provided'}
                            </Typography>
                        </Box>
                        <Button variant="outlined" onClick={downloadTemplate}>
                            Template
                        </Button>
                    </Stack>
                </DialogTitle>
                <DialogContent dividers>
                    {detailsLoading ? (
                        <Typography variant="body2" color="text.secondary">Loading camp details...</Typography>
                    ) : (
                        <Box sx={{ display: 'grid', gap: 2 }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Address</Typography>
                                <Typography variant="body2">
                                    {[details?.address, details?.locality, details?.city, details?.state, details?.pincode]
                                        .filter(Boolean)
                                        .join(', ') || '-'}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Order IDs</Typography>
                                <Typography variant="body2">{details?.orders?.join(', ') || '-'}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Slots</Typography>
                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
                                    {slots.map((slot) => (
                                        <Chip
                                            key={`${slot.date}|${slot.time}`}
                                            label={`${slot.date} ${slot.time}`}
                                            variant={selectedSlotKey === `${slot.date}|${slot.time}` ? 'filled' : 'outlined'}
                                            color={selectedSlotKey === `${slot.date}|${slot.time}` ? 'primary' : 'default'}
                                            onClick={() => setSelectedSlotKey(`${slot.date}|${slot.time}`)}
                                        />
                                    ))}
                                    {!slots.length && <Typography variant="body2" color="text.secondary">No slots found</Typography>}
                                </Stack>
                            </Box>
                            <Divider />
                            <Box sx={{ display: 'grid', gap: 2 }}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'flex-start' }} spacing={1.5}>
                                    <Box>
                                        <Typography variant="subtitle2">Bulk Camp Booking</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Upload CSV/XLSX phone numbers for the selected camp and slot.
                                        </Typography>
                                    </Box>
                                    <FormControlLabel
                                        sx={{ mr: 0 }}
                                        control={
                                            <Checkbox
                                                checked={bulkForm.isVip}
                                                onChange={(e) => updateBulkForm('isVip', e.target.checked)}
                                            />
                                        }
                                        label="VIP"
                                    />
                                </Stack>
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
                                    <Autocomplete
                                        multiple
                                        size="small"
                                        options={packageOptions}
                                        value={bulkPackages}
                                        loading={catalogLoading}
                                        onChange={(_event, value) => setBulkPackages(value)}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        getOptionLabel={(option) => `${option.name || option.id}${option.id ? ` (${option.id})` : ''}`}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Packages"
                                                helperText="Required"
                                            />
                                        )}
                                    />
                                    <Autocomplete
                                        multiple
                                        size="small"
                                        options={testOptions}
                                        value={bulkTests}
                                        loading={catalogLoading}
                                        onChange={(_event, value) => setBulkTests(value)}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        getOptionLabel={(option) => `${option.name || option.id}${option.id ? ` (${option.id})` : ''}`}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Tests"
                                                helperText="Optional"
                                            />
                                        )}
                                    />
                                    <TextField
                                        label="Pincode"
                                        size="small"
                                        value={bulkForm.pincode}
                                        onChange={(e) => updateBulkForm('pincode', e.target.value)}
                                        helperText="Optional; falls back to user pincode"
                                        sx={{ maxWidth: { md: 320 } }}
                                    />
                                </Box>
                                <TextField
                                    label="Notes"
                                    size="small"
                                    value={bulkForm.notes}
                                    onChange={(e) => updateBulkForm('notes', e.target.value)}
                                    multiline
                                    minRows={3}
                                />
                                {bulkResult && (
                                    <Alert severity={bulkResult.failedCount ? 'warning' : 'success'}>
                                        Created {bulkResult.createdCount} of {bulkResult.totalRows}. Failed {bulkResult.failedCount}.
                                    </Alert>
                                )}
                                {bulkResult?.failed?.length ? (
                                    <Box sx={{ maxHeight: 160, overflow: 'auto' }}>
                                        {bulkResult.failed.slice(0, 10).map((row) => (
                                            <Typography key={row.rowNumber} variant="caption" display="block" color="error">
                                                Row {row.rowNumber}: {row.error}
                                            </Typography>
                                        ))}
                                    </Box>
                                ) : null}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                    <Button onClick={() => setCampDetailsOpen(false)}>Close</Button>
                    <Button variant="outlined" component="label">
                        {bulkFile ? bulkFile.name : 'Upload CSV/XLSX'}
                        <input
                            hidden
                            type="file"
                            accept=".csv,.xlsx,.xls"
                            onChange={(e) => setBulkFile(e.target.files?.[0] || null)}
                        />
                    </Button>
                    <Button
                        variant="contained"
                        onClick={submitBulkOrders}
                        disabled={!selectedSlot || !bulkFile || bulkSaving}
                    >
                        {bulkSaving ? 'Creating...' : 'Create Bulk Bookings'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={orderOpen} onClose={() => setOrderOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Place Camp Order</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, pt: 1 }}>
                        <FormControl>
                            <InputLabel>User Type</InputLabel>
                            <Select
                                label="User Type"
                                value={orderUserType}
                                disabled={isHR}
                                onChange={(e) => handleOrderUserTypeChange(e.target.value as 'normal' | 'corporate')}
                            >
                                {!isHR && <MenuItem value="normal">Normal User</MenuItem>}
                                <MenuItem value="corporate">Corporate User</MenuItem>
                            </Select>
                        </FormControl>
                        {orderUserType === 'corporate' && (
                            <Autocomplete
                                options={corporateOptions}
                                value={selectedOrderCorporate}
                                loading={corporatesLoading}
                                disabled={isHR}
                                onChange={(_event, value) => handleOrderCorporateSelect(value)}
                                onInputChange={(_event, value, reason) => {
                                    if (isHR) return;
                                    if (reason === 'input') {
                                        setCorporateSearch(value);
                                    }
                                }}
                                isOptionEqualToValue={(option, value) => option._id === value._id}
                                getOptionLabel={(option) => option.name || option.companyName || option.email || option._id || ''}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select Corporate"
                                        required
                                        helperText={isHR ? 'Corporate is locked to your profile' : undefined}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {corporatesLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        )}
                        <Autocomplete
                            options={orderUserOptions}
                            value={selectedOrderUser}
                            loading={orderUsersLoading}
                            disabled={orderUserType === 'corporate' && !selectedOrderCorporate}
                            onChange={(_event, value) => handleOrderUserSelect(value)}
                            onInputChange={(_event, value, reason) => {
                                if (reason === 'input') {
                                    setOrderUserSearch(value);
                                }
                            }}
                            isOptionEqualToValue={(option, value) => option._id === value._id}
                            getOptionLabel={(option) =>
                                [option.name, option.phone, (option as any).corporateEmail || option.email]
                                    .filter(Boolean)
                                    .join(' - ')
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select User"
                                    required
                                    helperText={orderUserType === 'corporate' && !selectedOrderCorporate ? 'Select a corporate first' : undefined}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {orderUsersLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                        <Button
                            variant="outlined"
                            onClick={openCreateUserDialog}
                            disabled={orderUserType === 'corporate' && !selectedOrderCorporate && !scopedCorporateId}
                        >
                            Create User
                        </Button>
                        <TextField label="User ID" value={orderForm.userId} required disabled />
                        <FormControl required disabled={!slots.length}>
                            <InputLabel>Appointment Slot</InputLabel>
                            <Select
                                label="Appointment Slot"
                                value={selectedSlotKey}
                                onChange={(e) => setSelectedSlotKey(e.target.value)}
                            >
                                {slots.map((slot) => (
                                    <MenuItem key={`${slot.date}|${slot.time}`} value={`${slot.date}|${slot.time}`}>
                                        {buildAppointment(slot)}
                                    </MenuItem>
                                ))}
                                {!slots.length && <MenuItem value="" disabled>No slots available</MenuItem>}
                            </Select>
                        </FormControl>
                        <TextField label="Patient Name" value={orderForm.name} onChange={(e) => updateOrderForm('name', e.target.value)} required />
                        <TextField label="Mobile" value={orderForm.mobile} onChange={(e) => updateOrderForm('mobile', e.target.value)} required />
                        <FormControl>
                            <InputLabel>Gender</InputLabel>
                            <Select label="Gender" value={orderForm.gender} onChange={(e) => updateOrderForm('gender', e.target.value)}>
                                <MenuItem value="MALE">MALE</MenuItem>
                                <MenuItem value="FEMALE">FEMALE</MenuItem>
                                <MenuItem value="OTHER">OTHER</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField label="Email" value={orderForm.email} onChange={(e) => updateOrderForm('email', e.target.value)} />
                        <TextField label="Age" value={orderForm.age} onChange={(e) => updateOrderForm('age', e.target.value)} />
                        <TextField type="date" label="Date of Birth" value={orderForm.dateOfBirth} onChange={(e) => updateOrderForm('dateOfBirth', e.target.value)} InputLabelProps={{ shrink: true }} />
                        <Autocomplete
                            multiple
                            options={packageOptions}
                            value={orderPackages}
                            loading={catalogLoading}
                            onChange={(_event, value) => setOrderPackages(value)}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            getOptionLabel={(option) => `${option.name || option.id}${option.id ? ` (${option.id})` : ''}`}
                            renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Packages"
                                        helperText="Required"
                                    />
                                )}
                            />
                        <Autocomplete
                            multiple
                            options={testOptions}
                            value={orderTests}
                            loading={catalogLoading}
                            onChange={(_event, value) => setOrderTests(value)}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            getOptionLabel={(option) => `${option.name || option.id}${option.id ? ` (${option.id})` : ''}`}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Tests"
                                    helperText="Optional"
                                />
                            )}
                        />
                        <TextField label="Unique ID" value={orderForm.uniqueId} disabled />
                        <TextField label="Reference ID" value={orderForm.referenceId} onChange={(e) => updateOrderForm('referenceId', e.target.value)} />
                        <TextField label="Lab ID" value={orderForm.labId} onChange={(e) => updateOrderForm('labId', e.target.value)} />
                        <TextField label="Pincode" value={orderForm.pincode} onChange={(e) => updateOrderForm('pincode', e.target.value)} />
                        <TextField label="Alternate Contact" value={orderForm.altContact} onChange={(e) => updateOrderForm('altContact', e.target.value)} />
                        <TextField label="Notes" value={orderForm.notes} onChange={(e) => updateOrderForm('notes', e.target.value)} multiline minRows={2} sx={{ gridColumn: { xs: 'auto', sm: '1 / -1' } }} />
                        <FormControlLabel
                            control={<Checkbox checked={orderForm.isVip} onChange={(e) => updateOrderForm('isVip', e.target.checked)} />}
                            label="VIP"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOrderOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={submitOrder} disabled={saving || !selectedSlot}>
                        {saving ? 'Placing...' : 'Place Order'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={createUserOpen} onClose={() => setCreateUserOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Create User</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
                        <TextField
                            label="Name"
                            value={createUserForm.name}
                            onChange={(e) => updateCreateUserForm('name', e.target.value)}
                            required
                        />
                        <TextField
                            label="Email"
                            type="email"
                            value={createUserForm.email}
                            onChange={(e) => updateCreateUserForm('email', e.target.value)}
                            required
                        />
                        <TextField
                            label="Mobile"
                            value={createUserForm.phone}
                            onChange={(e) => updateCreateUserForm('phone', e.target.value)}
                            required
                        />
                        <FormControl>
                            <InputLabel>Gender</InputLabel>
                            <Select
                                label="Gender"
                                value={createUserForm.gender}
                                onChange={(e) => updateCreateUserForm('gender', e.target.value)}
                            >
                                <MenuItem value="Male">Male</MenuItem>
                                <MenuItem value="Female">Female</MenuItem>
                                <MenuItem value="Other">Other</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateUserOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={createUserFromOrder} disabled={createUserSaving}>
                        {createUserSaving ? 'Creating...' : 'Create User'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={visibilityOpen} onClose={() => setVisibilityOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Camp Visibility</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            {visibilityCamp?.campLsId} — choose which corporates can see and book this camp.
                            Leave empty to make it visible to everyone.
                        </Typography>
                        <Autocomplete
                            multiple
                            options={allCorporates}
                            value={visibilityCorporates}
                            onChange={(_event, value) => setVisibilityCorporates(value)}
                            isOptionEqualToValue={(option, value) => option._id === value._id}
                            getOptionLabel={(option) => option.name || option.companyName || option._id || ''}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Restrict to corporates"
                                    helperText="Empty = visible to all corporates and normal users"
                                />
                            )}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setVisibilityOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={saveVisibility} disabled={visibilitySaving}>
                        {visibilitySaving ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Layout>
    );
};

export default Camps;
